import { Trans } from '@lingui/react/macro';
import { useIsFetching } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { BrandSparkle } from './mag.js';

/**
 * Sticky "we're refreshing this page" pill, anchored top-center just
 * below the TopNav (top:72px). Fires for in-page react-query refetches
 * — the post-mutation refresh pattern, background query revalidation —
 * so the founder can tell the app is rebinding to fresh data.
 *
 * Deliberately NOT shown during route transitions. The thin yellow
 * RouterProgress bar covers those (navigations have their own visual
 * signal — the page itself is changing). Showing both at once was
 * noisy and conflated "I'm loading a new page" with "I'm refreshing
 * what's in front of me."
 *
 * Sub-150ms blips are debounced out so we don't flash on instant
 * cache hits.
 */
/** Routes that ARE meant to constantly poll — their own UI already
 *  surfaces "mag is working" state, so the global refetch pill on top
 *  of it just reads as noise. */
const SUPPRESS_PATH_PATTERNS: ReadonlyArray<RegExp> = [
  /\/onboarding\/scrape/, // live "mag is reading your site" surface
];

export function RefetchIndicator() {
  const inFlight = useIsFetching();
  const { routerLoading, currentPath } = useRouterState({
    select: (s) => ({
      routerLoading: s.status === 'pending' || s.isLoading || s.isTransitioning,
      currentPath: s.location.pathname,
    }),
  });
  const suppressed = SUPPRESS_PATH_PATTERNS.some((rx) => rx.test(currentPath));
  // Suppress the pill while the router is mid-transition — that's
  // route-load territory, RouterProgress owns it. Only surface for
  // refetches that happen while the founder is sitting on a page.
  // Also suppress on routes that explicitly own the "mag is working"
  // surface themselves (live scrape page polls every 1.5s — the pill
  // would never go away there).
  const busy = inFlight > 0 && !routerLoading && !suppressed;

  // Show only after busy holds for ~150ms — cache hits resolve in a few
  // ms and we don't want the pill to strobe every navigation. Once
  // visible, hold for at least 250ms after the last in-flight clears
  // so it doesn't pop in and out during a tight refetch sequence.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (busy) {
      const id = window.setTimeout(() => setVisible(true), 150);
      return () => window.clearTimeout(id);
    }
    const id = window.setTimeout(() => setVisible(false), 250);
    return () => window.clearTimeout(id);
  }, [busy]);

  return (
    <AnimatePresence>
      {visible ? (
        <motion.div
          aria-hidden={false}
          // Slide down from above the nav with a fade — anchored top-
          // center under the TopNav (top:72px). Motion takes over the
          // x-axis centering via `x: '-50%'` so its y-translation
          // doesn't conflict with a Tailwind `-translate-x-1/2`
          // (single transform property). Easing matches the
          // image-preview-modal + header chip dropdowns
          // (ease-out-quart) so every popover-shaped surface speaks
          // the same motion language.
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          style={{ x: '-50%' }}
          className="pointer-events-none fixed left-1/2 top-[72px] z-40"
        >
          <div className="flex items-center gap-2 rounded-full border border-[var(--line)] bg-white/95 px-3 py-1.5 shadow-[2px_2px_0_var(--plum)] backdrop-blur">
            {/* mag-spark-spin (not mag-spark-twinkle): the refresh pill
                signals active work, so the sparkle gets a real 2s
                continuous spin instead of the brand-mark twinkle's
                6s rocking. Distinct semantic per the css comment.
                stroke="var(--ink)" gives the yellow rays a hairline
                ink outline — at 16px against a near-white pill bg
                the fill alone fades; the outline gives each ray a
                silhouette without raising fill saturation. */}
            <BrandSparkle
              size={16}
              color="var(--spell)"
              stroke="var(--ink)"
              strokeWidth={0.6}
              strokeOpacity={0.25}
              className="mag-spark-spin"
            />
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/70">
              <Trans>refreshing</Trans>
            </span>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
